/* eslint-disable @typescript-eslint/no-unnecessary-condition */

import React from 'react'
import { Box } from '@mui/material'
import {
  CoreTemplateSectionPositionData,
  CoreTemplateIdentifierType,
  SECTIONS_SEPARATION_TECHNIQUE,
} from '@models'
import { CoreTemplatePageData } from '@query/common-query'
import { FeatureRenderer } from '@utils/hoc'
import { STYLE_SIZE_CONSTANTS } from '@utils/styles'
import { getCoreTemplateSectionRendererProps } from './getCoreTemplateSectionRendererProps'
import { ComponentTypeToComponentMapping } from './sectionComponents'

type SectionItem = CoreTemplateSectionPositionData<
  CoreTemplateIdentifierType<Record<string, unknown>>
>

const SECTION_SCROLL_MARGIN = {
  xs: `${STYLE_SIZE_CONSTANTS.mobileHeaderHeight + 16}px`,
  md: `${STYLE_SIZE_CONSTANTS.headerHeight + 16}px`,
} as const

export function CoreTemplatePage(
  props: CoreTemplatePageData & {
    sectionsSeparationTechnique?: SECTIONS_SEPARATION_TECHNIQUE
  }
) {
  const { sectionsSeparationTechnique, ...restSections } = props

  const sectionsOrderedList: SectionItem[] = Object.values(restSections)
    .flat()
    .filter((section): section is SectionItem => {
      return Boolean(section && 'type' in section)
    })
    .sort((a, b) => a.sectionPosition - b.sectionPosition)

  const sectionRendererPropsList = getCoreTemplateSectionRendererProps(
    sectionsOrderedList,
    sectionsSeparationTechnique
  )

  return (
    <>
      {sectionsOrderedList.map((item, index) => {
        const component = ComponentTypeToComponentMapping[item.type]
        const sectionRendererProps = sectionRendererPropsList[index]

        if (!component) {
          return null
        }

        const anchorId = item.id?.trim() || undefined
        const sectionData = (({ id, sectionId, ...rest }) => rest)(item)

        return (
          <Box
            key={`${item.type}-${item.sectionPosition}-${anchorId ?? index}`}
            component="section"
            id={anchorId}
            sx={
              anchorId
                ? {
                    scrollMarginTop: SECTION_SCROLL_MARGIN,
                  }
                : undefined
            }
          >
            <FeatureRenderer
              data={sectionData}
              Component={component}
              props={sectionRendererProps}
            />
          </Box>
        )
      })}
    </>
  )
}
